import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

type Row = {
  triggered_at: string;
  completed_at: string | null;
  outcome: "resolved" | "still_failing" | "skipped_locked" | "route_missing" | "error" | "cancelled";
};

type Bucket = {
  label: string;
  ts: number;
  succeeded: number;
  failed: number;
  skipped_locked: number;
  in_flight: number;
};

const RANGES = {
  "1h":  { ms:  60 * 60 * 1000, step:  60 * 1000,       fmt: (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  "6h":  { ms:  6 * 60 * 60 * 1000, step:  5 * 60 * 1000, fmt: (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  "24h": { ms: 24 * 60 * 60 * 1000, step: 30 * 60 * 1000, fmt: (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  "7d":  { ms:  7 * 24 * 60 * 60 * 1000, step: 6 * 60 * 60 * 1000, fmt: (d: Date) => d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + d.getHours() + "h" },
} as const;
type RangeKey = keyof typeof RANGES;

export function HelpdeskRetryThroughputChart() {
  const [range, setRange] = useState<RangeKey>("6h");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - RANGES[range].ms).toISOString();
      const { data, error } = await supabase
        .from("helpdesk_alert_retry_audit" as any)
        .select("triggered_at, completed_at, outcome")
        .gte("triggered_at", since)
        .order("triggered_at", { ascending: true })
        .limit(5000);
      if (cancelled) return;
      setLoading(false);
      if (error) return;
      setRows((data ?? []) as unknown as Row[]);
    })();
    return () => { cancelled = true; };
  }, [range]);

  const buckets = useMemo<Bucket[]>(() => {
    const { ms, step, fmt } = RANGES[range];
    const now = Date.now();
    const start = Math.floor((now - ms) / step) * step;
    const count = Math.ceil(ms / step);
    const out: Bucket[] = [];
    for (let i = 0; i < count; i++) {
      const ts = start + i * step;
      out.push({ label: fmt(new Date(ts)), ts, succeeded: 0, failed: 0, skipped_locked: 0, in_flight: 0 });
    }
    const bucketIndex = (t: number) => Math.floor((t - start) / step);

    for (const r of rows) {
      const startT = new Date(r.triggered_at).getTime();
      const endT = r.completed_at ? new Date(r.completed_at).getTime() : startT;
      const finalBucket = bucketIndex(endT);
      if (finalBucket >= 0 && finalBucket < count) {
        const b = out[finalBucket];
        if (r.outcome === "resolved") b.succeeded++;
        else if (r.outcome === "skipped_locked") b.skipped_locked++;
        else b.failed++; // still_failing, error, route_missing, cancelled
      }
      // "in-flight": count the row in every bucket between start and end (proxy for concurrency spike)
      const from = Math.max(0, bucketIndex(startT));
      const to = Math.min(count - 1, finalBucket);
      for (let i = from; i <= to; i++) {
        if (i >= 0) out[i].in_flight++;
      }
    }
    return out;
  }, [rows, range]);

  const totals = useMemo(() => buckets.reduce(
    (acc, b) => ({
      succeeded: acc.succeeded + b.succeeded,
      failed: acc.failed + b.failed,
      skipped_locked: acc.skipped_locked + b.skipped_locked,
      peak_in_flight: Math.max(acc.peak_in_flight, b.in_flight),
    }),
    { succeeded: 0, failed: 0, skipped_locked: 0, peak_in_flight: 0 },
  ), [buckets]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="size-4" /> Bulk retry throughput
          </CardTitle>
          <CardDescription>
            Retry attempts bucketed over time — succeeded, failed, and skipped-because-locked, with peak
            concurrent in-flight as a line. Spot overload spikes and lock contention at a glance.
          </CardDescription>
        </div>
        <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">Last 1h</SelectItem>
            <SelectItem value="6h">Last 6h</SelectItem>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7d</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex flex-wrap gap-3 text-xs">
          <div><span className="text-muted-foreground">Succeeded:</span> <span className="font-medium text-emerald-600 dark:text-emerald-400">{totals.succeeded}</span></div>
          <div><span className="text-muted-foreground">Failed:</span> <span className="font-medium text-destructive">{totals.failed}</span></div>
          <div><span className="text-muted-foreground">Skipped (locked):</span> <span className="font-medium text-amber-600 dark:text-amber-400">{totals.skipped_locked}</span></div>
          <div><span className="text-muted-foreground">Peak in-flight:</span> <span className="font-medium">{totals.peak_in_flight}</span></div>
        </div>

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="size-4 animate-spin inline" /></div>
        ) : (
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ left: 4, right: 4, top: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="succeeded" stackId="a" fill="hsl(160 60% 45%)" name="succeeded" />
                <Bar dataKey="failed" stackId="a" fill="hsl(var(--destructive))" name="failed" />
                <Bar dataKey="skipped_locked" stackId="a" fill="hsl(38 92% 50%)" name="skipped (locked)" />
                <Bar dataKey="in_flight" fill="hsl(var(--primary))" name="in-flight (peak)" opacity={0.35} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskRetryThroughputChart;
